import { useDeliveryLocationCookie } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocationCookie';
import { getEddConfig } from '@/data/config/eddConfig';
import { isEddPlpEnabled } from '@/data/config/eddPlpToggle';
import { TYPES } from '@/data/constants/product';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import {
	getEddMinQuantity,
	HomeDeliveryDateState,
	resolveHomeDeliveryDateState,
	useBlockedZipCodes,
} from '@/data/Content/HomeDeliveryDate';
import { getLeadTime, getLeadTimeStoreIds } from '@/data/Content/LeadTime';
import { PickupDateState, resolvePickupDateState } from '@/data/Content/PickupDate';
import { isProductStoreOnlyAvailable } from '@/data/Content/useIsProductStoreOnlyAvailable';
import { usePhysicalStoreData } from '@/data/Content/usePhysicalStoreData';
import { useSettings } from '@/data/Settings';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { ProductType } from '@/data/types/Product';
import {
	getComboStock,
	getMarketAvailability,
	getPickupAvailability,
} from '@/data/utils/eddInventory';
import {
	isDeliveryOnlyInStore,
	isProductFlagEnabled,
	isSameDayFlagEnabled,
} from '@/data/utils/eddProductFlags';
import { mapProductDetailsData } from '@/data/utils/mapProductDetailsData';
import { useIsClient } from '@/data/utils/useIsClient';
import { useMemo } from 'react';
import { useCookies } from 'react-cookie';
import {
	DEFAULT_STORE_ID,
	GEO_LOCATION_COOKIE_NAME,
} from 'shared/custom/constants/commonConstants';
import { getFulfillmentType } from 'shared/custom/utils/inventoryUtils';

export type PlpEddState = {
	delivery: HomeDeliveryDateState;
	pickup: PickupDateState;
};

export type PlpEstimatedDeliveryStates = {
	/** Keyed by the listing product's `id`, so callers can look up by the product they rendered. */
	states: Map<string, PlpEddState>;
	/** Whether every input the resolution needs has settled. */
	isResolved: boolean;
};

/** A channel the card never renders a label for resolves to the settled "no label" state. */
const NO_EDD: PlpEddState = {
	delivery: { status: 'no-label' },
	pickup: { status: 'no-label' },
};

/**
 * The product whose EDD the card actually resolves: `PLPProductCard` renders
 * `<DeliveryPickupDates product={sku ?? product} />`, where `sku` is the default
 * selection `useProductCard` derives from `product.items`. A colour swatch can
 * change that selection later, which is why this reads the default one only — the
 * listing event must not move when the customer interacts with a card.
 */
const getEddTargetProduct = (product: ProductType): ProductType => {
	if (!product.items) return product;
	const { selection } = mapProductDetailsData(product, undefined);
	return selection?.sku ?? product;
};

/**
 * Whether the listing in hand still belongs to a different store than the selected
 * one. The store comes from a cookie and flips instantly, while the listing is a
 * request that lands seconds later, so there is a window where the two disagree.
 *
 * Every input of both channels is read off the listing payload keyed by store —
 * `x_itemleadtime.<storeId>` and `inventories.<uniqueId>.quantity` — so during that
 * window delivery resolves `no-label` for everything (no lead time for the new
 * store) and pickup silently falls back to "availability unknown, assume
 * available". Neither reflects what the customer will be shown once the listing
 * catches up.
 *
 * The search response only ever projects the store it was queried with, so the set
 * of lead-time stores across the listing identifies which store it belongs to. An
 * empty set means the payload carries no lead times at all, which is a settled
 * answer rather than a stale one.
 */
export const isListingStaleForStore = (
	products: ProductType[],
	physicalStoreId: string
): boolean => {
	const listingStoreIds = new Set(products.flatMap((product) => getLeadTimeStoreIds(product)));
	return listingStoreIds.size > 0 && !listingStoreIds.has(physicalStoreId);
};

/**
 * Resolves the PLP delivery/pickup states for a whole listing in one synchronous
 * pass, without mounting a hook per product.
 *
 * This is possible because the PLP chain makes no per-product request: every input
 * either lives on the listing payload already (`x_itemleadtime.*`,
 * `inventories.*.quantity`, attributes, ribbons) or is global and shared
 * (storeconf, the blocked-zip list, the selected store). So there is no partial
 * state to wait out and no ordering between products to coordinate — `isResolved`
 * covers the whole listing at once.
 *
 * The per-product decisions come from the exact functions the labels use
 * (`resolveHomeDeliveryDateState`, `resolvePickupDateState`), so what this reports
 * and what the customer sees cannot drift apart.
 */
export const usePlpEstimatedDeliveryStates = (
	products: ProductType[] | undefined
): PlpEstimatedDeliveryStates => {
	const isClient = useIsClient();
	const { settings } = useSettings();
	const { physicalStoreId } = useStoreIDS();
	const { uniqueId } = useDefaultStore();
	const { zipCode } = useDeliveryLocationCookie();
	const { blockedZipCodes, isLoading: isLoadingBlockedZipCodes } = useBlockedZipCodes(
		!!products?.length
	);

	const [cookies] = useCookies([GEO_LOCATION_COOKIE_NAME]);
	const geoCookie = cookies?.[GEO_LOCATION_COOKIE_NAME];
	const storeOnlyStoreId =
		geoCookie?.hasPreviousAnswer && geoCookie?.stLocId ? geoCookie.stLocId : DEFAULT_STORE_ID;
	const { marketId } = usePhysicalStoreData({ physicalStoreId: storeOnlyStoreId });

	const eddTargets = useMemo(
		() =>
			(products ?? []).map((listingProduct) => ({
				listingProduct,
				product: getEddTargetProduct(listingProduct),
			})),
		[products]
	);

	const isStale = useMemo(
		() =>
			isListingStaleForStore(
				eddTargets.map(({ product }) => product),
				physicalStoreId
			),
		[eddTargets, physicalStoreId]
	);

	const isResolved = isClient && !isLoadingBlockedZipCodes && !isStale;

	const states = useMemo(() => {
		const resolved = new Map<string, PlpEddState>();
		if (!isResolved || !eddTargets.length) return resolved;

		const config = getEddConfig(settings.userData);
		const eddEnabled = isEddPlpEnabled(settings.userData);

		eddTargets.forEach(({ listingProduct, product }) => {
			const key = listingProduct.id;

			const hasNoLabels =
				!eddEnabled ||
				listingProduct.type === TYPES.bundle ||
				isProductStoreOnlyAvailable({ productAttributes: listingProduct.attributes, marketId });
			if (hasNoLabels) {
				resolved.set(key, NO_EDD);
				return;
			}

			const leadTime = getLeadTime(product, physicalStoreId);
			const minQuantity = getEddMinQuantity(product);
			const fulfillmentType = getFulfillmentType(
				product.attributes,
				(product.ribbons ?? []).map((ribbon) => ribbon.identifier)
			);
			const { storeAvailability, marketAvailability } = getPickupAvailability(product, {
				storeUniqueId: uniqueId,
				physicalStoreId,
			});
			const isCombo = product.type === TYPES.kit;

			resolved.set(key, {
				delivery: resolveHomeDeliveryDateState(config, {
					zipCode,
					blockedZipCodes,
					isBopisOnly: isProductFlagEnabled(product.bopisOnly),
					checkMarketAvailability: true,
					marketAvailability: getMarketAvailability(product, physicalStoreId),
					minQuantity,
					leadTime,
					fulfillmentType,
					isSameDayFlagOn: isSameDayFlagEnabled(product, physicalStoreId),
					storeAvailability,
				}),
				pickup: resolvePickupDateState(config, {
					isDeliveryOnly: isDeliveryOnlyInStore(product, physicalStoreId),
					fulfillmentType,
					isCombo,
					comboStock: isCombo
						? getComboStock(product.components ?? [], {
								storeUniqueId: uniqueId,
								physicalStoreId,
						  })
						: undefined,
					leadTime,
					minQuantity,
					storeAvailability,
					marketAvailability,
				}),
			});
		});

		return resolved;
	}, [
		isResolved,
		eddTargets,
		settings.userData,
		physicalStoreId,
		uniqueId,
		zipCode,
		blockedZipCodes,
		marketId,
	]);

	return { states, isResolved };
};
